import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { AlertCircle, Shield, Inbox, RefreshCw, Unlock, Wifi, WifiOff, ChevronRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { API, BACKEND_URL } from '@/config/api';

export default function AlertsPage({ token, user }) {
  const navigate = useNavigate();
  const [misuseAlerts, setMisuseAlerts] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rtConnected, setRtConnected] = useState(false);
  // Map "udt_id:viewer_id" -> latest evidence_id (for alerts that didn't get linked)
  const [evidenceMap, setEvidenceMap] = useState({});

  // Ensure only ONE WS connection + timers exist (prevents duplicates/leaks).
  const wsRef = useRef(null);
  const pingTimerRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const closedRef = useRef(false);
  const seenKeysRef = useRef(new Set());
  const seenOrderRef = useRef([]);
  const wsHealthyRef = useRef(false);
  const pollIntervalRef = useRef(null);

  const isDev = process.env.NODE_ENV === 'development';

  // BUG 1 FIX: computeKey must be defined at component level (before any useEffect)
  const computeKey = (alert) => {
    // Priority 1: evidence_id (MongoDB ObjectId — guaranteed unique)
    if (alert?.evidence_id) return alert.evidence_id;

    // Priority 2: composite key (fallback if evidence_id missing)
    if (alert?.timestamp && alert?.udt_id && alert?.violation_type) {
      return `${alert.udt_id}_${alert.violation_type}_${alert.timestamp}`;
    }

    // Priority 3: last resort
    return `${alert?.timestamp}_${alert?.message}`;
  };

  // Normalize evidence_id so UI can always rely on ev.evidence_id
  const normalizeEvidenceId = (alert) => {
    if (!alert) return alert;
    if (alert.evidence_id) return alert;

    const nestedId =
      alert?.evidence?.evidence_id ||
      (Array.isArray(alert?.evidence) && alert.evidence[0]?.evidence_id) ||
      alert?.proof_data?.evidence_id ||
      null;

    return nestedId ? { ...alert, evidence_id: nestedId } : alert;
  };

  // For legacy alerts without evidence_id, try to resolve a matching evidence_id
  // by looking up misuse/monitor evidence for the related UDTs.
  const buildEvidenceMapForAlerts = async (alertsList) => {
    try {
      const udtIds = Array.from(
        new Set(
          alertsList
            .filter((a) => !a.evidence_id && a.udt_id)
            .map((a) => a.udt_id)
        )
      );

      if (!udtIds.length || !token) return;

      const localMap = {};

      for (const udtId of udtIds) {
        try {
          // Fetch misuse + monitor evidence for this document (owner-only)
          const misusePromise = axios.get(`${API}/misuse/evidence/${udtId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });

          const monitorPromise = axios
            .get(`${API}/monitor/evidence/${udtId}`, {
              params: { include_preview: false },
              headers: { Authorization: `Bearer ${token}` }
            })
            .catch(() => ({ data: { evidence: [] } }));

          const [misuseRes, monitorRes] = await Promise.all([
            misusePromise,
            monitorPromise
          ]);

          const combined = [
            ...(misuseRes.data?.evidence || []),
            ...(monitorRes.data?.evidence || [])
          ];

          combined.forEach((ev) => {
            if (!ev?.evidence_id || !ev?.udt_id) return;
            const key = `${ev.udt_id}:${ev.viewer_id || ''}`;
            const ts =
              ev.timestamp_ms ||
              ev.ts_ms ||
              (ev.monitor && ev.monitor.timestamp_ms) ||
              new Date(
                ev.timestamp ||
                  ev.proof_data?.timestamp ||
                  ev.created_at ||
                  0
              ).getTime();

            // Try to capture the same violation_type Evidence Vault uses
            const violationType =
              ev.violation_type ||
              ev.event_type ||
              (ev.monitor && (ev.monitor.violation_type || ev.monitor.event_type)) ||
              (ev.proof_data && (ev.proof_data.violation_type || ev.proof_data.event_type)) ||
              null;

            const existing = localMap[key];
            if (!existing || ts > existing.ts) {
              localMap[key] = { id: ev.evidence_id, ts, violation_type: violationType };
            }
          });
        } catch (err) {
          console.error('[Alerts] Failed to resolve evidence for', udtId, err);
        }
      }

      if (Object.keys(localMap).length === 0) return;

      setEvidenceMap((prev) => {
        const next = { ...prev };
        Object.entries(localMap).forEach(([key, value]) => {
          const existing = next[key];
          if (!existing || value.ts > existing.ts) {
            next[key] = value;
          }
        });
        return next;
      });
    } catch (err) {
      console.error('[Alerts] buildEvidenceMapForAlerts failed', err);
    }
  };

  // formatViolation must be defined before WebSocket useEffect uses it
  // Keep this mapping in sync with EvidenceVault so titles match exactly.
  const formatViolation = (type) => {
    if (!type) return 'Misuse detected';
    const map = {
      MULTIPLE_FACES: 'Multiple faces detected',
      PHONE_CAMERA_DETECTED: 'Phone camera detected',
      MOTION_BURST_NEAR_FACE: 'Motion detected near face',
      CAMERA_FROZEN_LOCAL: 'Camera frozen / covered',
      CAMERA_BLOCKED_LOCAL: 'Camera blocked / covered',
      PHONE_CAMERA_SUSPECTED: 'Phone camera detected',
      SCREEN_RECORDING: 'Screen recording detected',
      TAB_SWITCH: 'Tab switch detected',
      TAB_SWITCH_VIOLATION: 'Tab switch detected',
      EXCESSIVE_TAB_SWITCH: 'Excessive tab switching',
      KEYBOARD_SHORTCUT: 'Restricted keyboard shortcut',
      screenshot_attempt: 'Screenshot attempt',
      screenshot_detected: 'Screenshot detected',
      download_attempt: 'Unauthorized download attempt',
      print_attempt: 'Unauthorized print attempt',
      no_face: 'No face detected',
      NO_FACE_DETECTED: 'No face detected',
      phone_camera: 'Phone camera detected',
      camera_blocked: 'Camera blocked / covered',
      FACE_DISAPPEARED: 'Face disappeared',
      monitor_event: 'Monitoring event',
      system_error: 'AI monitoring error',
      UNKNOWN: 'Unknown violation'
    };
    return map[type] || String(type).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  // Normalize violation type from any plausible alert fields so Alerts matches Evidence Vault.
  const getAlertViolationType = (ev) => {
    if (!ev) return '';
    return (
      ev.violation_type ||
      (ev.monitor && (ev.monitor.violation_type || ev.monitor.event_type)) ||
      (ev.metadata && (ev.metadata.violation_type || ev.metadata.event_type || ev.metadata.type)) ||
      ev.event_type ||
      ev.type ||
      'UNKNOWN'
    );
  };

  // Smart polling helpers — only poll when WebSocket is unhealthy
  const stopPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  };

  const pollUnreadOnce = async () => {
    // If WS recovered, stop polling.
    if (wsHealthyRef.current) {
      stopPolling();
      return;
    }

    try {
      const resp = await axios.get(`${API}/alerts/unread`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (resp.data.alerts?.length > 0) {
        console.log('[Dashboard] Polled alerts:', resp.data.alerts.length);
        const normalized = resp.data.alerts.map(normalizeEvidenceId);
        setMisuseAlerts((prev) => {
          const existingKeys = new Set(prev.map((a) => computeKey(a)));
          const newOnly = normalized.filter(
            (a) => !existingKeys.has(computeKey(a))
          );
          return newOnly.length > 0 ? [...newOnly, ...prev] : prev;
        });
      }
    } catch (err) {
      console.error('[Dashboard] Alert poll failed:', err);
    }
  };

  const startPolling = (intervalMs) => {
    stopPolling();
    pollIntervalRef.current = setInterval(pollUnreadOnce, intervalMs);
  };

  const wsUrl = useMemo(() => {
    const base = (BACKEND_URL || '').trim().replace(/\/+$/, '');
    if (!base) return '';
    const wsBase = base.startsWith('https://') ? base.replace('https://', 'wss://') : base.replace('http://', 'ws://');
    // TODO(security): migrate to short-lived WS tickets minted via REST.
    return `${wsBase}/ws/alerts?token=${encodeURIComponent(token || '')}`;
  }, [token]);

  useEffect(() => {
    fetchAlerts();
  }, []);

  // BUG 2 FIX: WebSocket with smart polling fallback
  useEffect(() => {
    if (!token || !wsUrl) return;

    let ws = null;
    let reconnectTimer = null;
    let isMounted = true;

    const remember = (key) => {
      if (!key) return false;
      const seen = seenKeysRef.current;
      if (seen.has(key)) return false;
      seen.add(key);
      seenOrderRef.current.push(key);
      if (seen.size > 1500) {
        const old = seenOrderRef.current.shift();
        if (old) seen.delete(old);
      }
      return true;
    };

    const connect = () => {
      if (!isMounted) return;

      try {
        ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          if (!isMounted) {
            ws.close();
            return;
          }
          console.log('[Dashboard] WebSocket connected');
          wsHealthyRef.current = true;
          stopPolling(); // WS healthy — stop polling
          setRtConnected(true);
          reconnectAttemptRef.current = 0;

          // Start ping interval
          pingTimerRef.current = setInterval(() => {
            try {
              if (ws && ws.readyState === WebSocket.OPEN && isMounted) {
                ws.send(JSON.stringify({ type: 'ping', ts_ms: Date.now() }));
              }
            } catch {}
          }, 25000);
        };

        ws.onmessage = (event) => {
          if (!isMounted) return;
          
          try {
            const data = JSON.parse(event.data);
            console.log('[Dashboard] WS message received:', data.type, data);

            if (data.type === 'pong') return;

            if (data.type === 'security_alert') {
              const incoming = data.alert || data;
              const normalized = normalizeEvidenceId(incoming);
              setMisuseAlerts((prev) => [normalized, ...prev]);
              return;
            }
          } catch (err) {
            console.error('[Dashboard] WS parse error:', err);
          }
        };

        ws.onerror = (err) => {
          console.warn('[Dashboard] WS error — will use polling fallback');
        };

        ws.onclose = () => {
          if (!isMounted) return;
          
          setRtConnected(false);
          wsHealthyRef.current = false;
          
          // Clear ping timer
          if (pingTimerRef.current) {
            clearInterval(pingTimerRef.current);
            pingTimerRef.current = null;
          }

          // Start fallback polling + reconnect after 5 seconds
          console.log('[Dashboard] WS closed — starting fallback polling and reconnect');
          startPolling(5000); // poll every 5s while WS is down
          reconnectTimer = setTimeout(() => {
            if (isMounted) {
              connect();
            }
          }, 5000);
        };
      } catch (err) {
        console.error('[Dashboard] WS init failed:', err);
        if (isMounted) {
          setRtConnected(false);
          reconnectTimer = setTimeout(() => {
            if (isMounted) {
              connect();
            }
          }, 5000);
        }
      }
    };

    connect();

    return () => {
      isMounted = false;
      wsHealthyRef.current = false;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      stopPolling();
      if (pingTimerRef.current) {
        clearInterval(pingTimerRef.current);
        pingTimerRef.current = null;
      }
      if (ws) {
        try {
          ws.close();
        } catch {}
      }
      wsRef.current = null;
      setRtConnected(false);
    };
  }, [token, wsUrl]);

  const fetchAlerts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/alerts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('[Alerts] REST response:', response.data);

      const data = response.data;
      const alertList = Array.isArray(data)
        ? data
        : (data.alerts || data.misuse_alerts || data.data || []);

      const normalizedAlerts = alertList.map(normalizeEvidenceId);
      setMisuseAlerts(normalizedAlerts);
      // For alerts that still don't carry an evidence_id, try to resolve from Evidence Vault data.
      buildEvidenceMapForAlerts(normalizedAlerts);
      // Keep pendingRequests if the payload still provides it; otherwise default empty.
      setPendingRequests(data.pending_requests || []);
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
      setMisuseAlerts([]);
      setPendingRequests([]);
      toast.error('Failed to fetch alerts');
    } finally {
      setLoading(false);
    }
  };

  // Derive violation type from various possible locations on the alert payload
  const getViolationType = (ev) => {
    if (!ev) return '';
    return (
      ev.violation_type ||
      (ev.metadata && (ev.metadata.violation_type || ev.metadata.type)) ||
      (ev.monitor && (ev.monitor.violation_type || ev.monitor.type)) ||
      (ev.proof_data && (ev.proof_data.violation_type || ev.proof_data.type)) ||
      ''
    );
  };

  const getAlertAccent = (ev, overrideType) => {
    const action = String(ev?.monitor?.action || '').toLowerCase();
    if (action === 'revoke') return '#dc2626';
    if (action === 'lock') return '#ef4444';
    if (action === 'warn') return '#f59e0b';
    // fallback by violation type
    const t = String(overrideType || getAlertViolationType(ev) || '');
    if (t === 'MULTIPLE_FACES') return '#ef4444';
    if (t === 'phone_camera' || t === 'PHONE_CAMERA_DETECTED') return '#f97316';
    if (t === 'camera_blocked') return '#dc2626';
    if (t === 'no_face') return '#f59e0b';
    return '#64748b';
  };

  const allowAgain = async (udtId, recipientId) => {
    try {
      await axios.post(`${API}/access/allow-again`, {
        udt_id: udtId,
        recipient_id: recipientId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Access restored for recipient');
      fetchAlerts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore access');
    }
  };

  return (
    <DashboardLayout user={user} token={token}>
      <div className="container mx-auto px-4 py-4 sm:py-6 lg:py-8 w-full min-w-0">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 flex-wrap">
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold" style={{ color: '#f59e0b' }}>Alerts & Notifications</h1>
            <p className="text-xs sm:text-sm" style={{ color: '#666' }}>All system alerts and notifications</p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="flex items-center gap-1">
              {rtConnected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
              {rtConnected ? 'Realtime: ON' : 'Realtime: OFF'}
            </Badge>
            <Button variant="outline" onClick={fetchAlerts} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        <div className="max-w-4xl mx-auto">
          <Tabs defaultValue="misuse" className="space-y-4">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="misuse" className="flex items-center gap-2">
                <Shield className="w-4 h-4" />
                Misuse Alerts
                {misuseAlerts.length > 0 && <Badge className="ml-1 bg-red-500">{misuseAlerts.length}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="requests" className="flex items-center gap-2">
                <Inbox className="w-4 h-4" />
                Pending Requests
                {pendingRequests.length > 0 && <Badge className="ml-1">{pendingRequests.length}</Badge>}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="misuse" className="space-y-4">
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
                </div>
              ) : misuseAlerts.length === 0 ? (
                <div className="text-center py-12">
                  <AlertCircle className="w-16 h-16 mx-auto mb-4" style={{ color: '#ccc' }} />
                  <p className="text-lg mb-2" style={{ color: '#666' }}>No misuse alerts</p>
                  <p className="text-sm" style={{ color: '#999' }}>All clear! No security violations detected.</p>
                </div>
              ) : (
                misuseAlerts.map((ev) => {
                  const alertEvidenceKey = `${ev.udt_id || ''}:${ev.viewer_id || ''}`;
                  const evidenceInfo = evidenceMap[alertEvidenceKey];
                  const evidenceViolationType = evidenceInfo?.violation_type || null;

                  const effectiveViolationType =
                    ev.violation_type ||
                    evidenceViolationType ||
                    getAlertViolationType(ev);

                  const documentLabel =
                    ev.document_name ||
                    (ev.document && ev.document.file_name) ||
                    ev.udt_id ||
                    'Unknown document';

                  const viewerId = ev.viewer_id || (ev.viewer && ev.viewer.user_id) || '';
                  const viewerName = ev.viewer_name || (ev.viewer && ev.viewer.name) || viewerId || 'Unknown viewer';
                  const viewerEmail =
                    ev.viewer_email ||
                    (ev.viewer && ev.viewer.email) ||
                    '';

                  const tsValue = ev.created_at || ev.timestamp || ev.ts_ms || Date.now();
                  const derivedEvidenceId =
                    ev.evidence_id ||
                    evidenceMap[alertEvidenceKey]?.id ||
                    null;

                  return (
                    <Card
                      key={ev.alert_id || derivedEvidenceId || `${ev.udt_id || 'alert'}_${ev.ts_ms || ev.timestamp || ''}`}
                      style={{ borderLeft: `4px solid ${getAlertAccent(ev, effectiveViolationType)}` }}
                    >
                      <CardHeader>
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3">
                            <Shield className="w-5 h-5" style={{ color: getAlertAccent(ev, effectiveViolationType) }} />
                            <div>
                              {/* Title: keep existing violation wording, e.g. "Tab switch detected" */}
                              <CardTitle className="text-lg">
                                {formatViolation(effectiveViolationType)}
                              </CardTitle>
                              <CardDescription className="mt-1 space-y-1">
                                <div className="flex items-center text-sm text-gray-700">
                                  <span className="mr-1">📄</span>
                                  <span className="font-medium">{documentLabel}</span>
                                </div>
                                <div className="flex items-center text-sm text-gray-700">
                                  <span className="mr-1">👤</span>
                                  <span>
                                    {viewerName !== viewerId && viewerEmail
                                      ? `${viewerName} • ${viewerEmail}`
                                      : (viewerName || viewerId || 'Unknown viewer')}
                                  </span>
                                </div>
                              </CardDescription>
                              {ev?.monitor && (
                                <div className="mt-2 flex flex-wrap gap-2">
                                  <Badge variant="outline">Monitor</Badge>
                                  {ev.monitor.action && (
                                    <Badge variant="outline">
                                      Action: {String(ev.monitor.action).toUpperCase()}
                                    </Badge>
                                  )}
                                  {ev.monitor.bucket && (
                                    <Badge variant="outline">{String(ev.monitor.bucket)}</Badge>
                                  )}
                                  {ev.monitor?.verdict?.class && (
                                    <Badge variant="outline">
                                      Verdict: {ev.monitor.verdict.class}
                                    </Badge>
                                  )}
                                  {ev.monitor_session_id && (
                                    <Badge variant="outline">
                                      Session: {String(ev.monitor_session_id).slice(0, 8)}…
                                    </Badge>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          <Badge variant="outline">
                            {new Date(tsValue).toLocaleString()}
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent className="flex items-center justify-between flex-wrap gap-2">
                        <div className="text-sm text-gray-600">
                          Evidence ID: <span className="font-mono">{derivedEvidenceId || '—'}</span>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => navigate('/dashboard/evidence')}>
                            View Evidence Vault
                            <ChevronRight className="w-4 h-4 ml-1" />
                          </Button>
                          {ev.udt_id && ev.viewer_id && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => allowAgain(ev.udt_id, ev.viewer_id)}
                              style={{ borderColor: '#10b981', color: '#10b981' }}
                            >
                              <Unlock className="w-4 h-4 mr-1" />
                              Allow again
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </TabsContent>

            <TabsContent value="requests" className="space-y-4">
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
                </div>
              ) : pendingRequests.length === 0 ? (
                <div className="text-center py-12">
                  <AlertCircle className="w-16 h-16 mx-auto mb-4" style={{ color: '#ccc' }} />
                  <p className="text-lg mb-2" style={{ color: '#666' }}>No pending requests</p>
                  <p className="text-sm" style={{ color: '#999' }}>No one is waiting for access approval right now.</p>
                </div>
              ) : (
                pendingRequests.map((req) => (
                  <Card key={req.request_id} style={{ borderLeft: '4px solid #f59e0b' }}>
                    <CardHeader>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3">
                          <Inbox className="w-5 h-5" style={{ color: '#f59e0b' }} />
                          <div>
                            <CardTitle className="text-lg">Access request</CardTitle>
                            <CardDescription>
                              UDT: <span className="font-mono">{req.udt_id}</span> • {req.requester_name} ({req.requester_email})
                            </CardDescription>
                          </div>
                        </div>
                        <Badge className="bg-amber-100 text-amber-800">
                          {new Date(req.created_at).toLocaleString()}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="flex items-center justify-between flex-wrap gap-2">
                      <div className="text-sm text-gray-600">
                        Purpose: {req.purpose}
                      </div>
                      <Button size="sm" onClick={() => navigate('/dashboard/requests')} style={{ background: '#0F52BA' }}>
                        Review
                      </Button>
                    </CardContent>
                  </Card>
                ))
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </DashboardLayout>
  );
}
